import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { snakeCase, map, startCase, sortBy, isNull, isEmpty, isNil } from 'lodash'
import { IconInfoCircle, IconSparkles, IconTrash } from '@tabler/icons-react'
import { v4 as uuid } from 'uuid'
import moment from 'moment'
import { format, differenceInDays, subDays } from 'date-fns'
import pluralize from 'pluralize'

import { Loader } from '@/components/loaders/Loader'
import { PageHeader } from '@/components/headers/PageHeader'
import { formatPercent, hasAnyOmnichannelAccess } from '@/utils/helpers'
import { sendFullStoryEvent } from '@/utils/fullStoryHelpers'
import { Button } from '@/components/buttons/Button'
import { chartColors } from '@/utils/constants'
import { nativeTooltip } from '@/components/charts/helpers'
import {
  fetchTrendsFromLatestFilters,
  fetchTrendCountForOrg,
  deleteTrend,
  downloadTrendCallsCsv,
  updateTrendName,
} from '@/reducers/insights/insightEvents.actions'
import { NOT_APPLICABLE_CATEGORY, plottableTrendSeries } from '@/reducers/insights/insights.helpers'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { closeModal, openModal } from '@/reducers/ui/ui.redux'
import NoData from '@/components/NoData'
import { Banner } from '@/components/banners/Banner'
import { getTriggerLabel } from '@/components/helpers'
import { resetChatFeed } from '@/reducers/baltoGPT/baltoGPT.actions'

import {
  clearAllFilters,
  clearSavedFilter,
  setSavedFilterById,
} from '@/reducers/callSearch/callSearch.actions'
import { SavedFilters } from '@/components/savedFilters/SavedFilters'
import { CALL_EXPLORER_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { fetchSavedFilters } from '@/reducers/savedFilters/savedFilters.actions'
import { setDateRangeFilters, setFilters } from '@/reducers/callSearch/callSearch.redux'

import { InsightsModule } from './components/InsightsModule'
import { CallSearchFilters } from '../Calls/CallSearchFilters'
import { AI_AGENT_FILTERS, CHANNEL_FILTER, KEYWORD_FILTERS } from '../Calls/CallSearch.constants'
import { TrendNoDataState } from './components/TrendNoDataState'
import { EditableHeader } from '../../components/headers/EditableHeader'
import { SentimentModule } from './components/SentimentModule'
import { TrendEvidenceDrawer } from './components/TrendEvidenceDrawer'

import './Trends.scss'

// What the save-filter form offers on the board. Mirrors Discover's list minus
// the keyword fields, since the board hides keyword search - a saved filter
// should not be able to carry a filter this page cannot apply.
const SAVED_FILTER_FORM_OPTIONS = [
  'name',
  'isDefault',
  'filterPropertiesHeader',
  'dateRange',
  'agents',
  'tags',
  'playbooks',
  'callDuration',
  'maxCallDuration',
  'isWin',
  'playbookPropertiesHeader',
  'checklist',
  'deck',
  'notifications',
  'postcall',
  'scorecards',
  'scorecardThreshold',
  'scoredStatus',
]

export const InsightsPage = () => {
  const dispatch = useDispatch()
  const { organizationid } = useSelector((state) => state.currentUser)
  const {
    trends,
    loading: { trends: trendsLoading },
    allow_creation,
  } = useSelector((state) => state.trends)
  const { currentlyOpenModalId } = useSelector((state) => state.ui)
  const { organizations } = useSelector((state) => state)
  const [lastNDays, setLastNDays] = useState(null)
  const [lineChartData, setLineChartData] = useState(null)
  const [barChartData, setBarChartData] = useState(null)
  const [countOfNonApplicableCalls, setCountOfNonApplicableCalls] = useState([])
  const MAX_CATEGORIES = 5
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const isBaltoAdmin = organizationid === 1
  const [selectedOrgId, setSelectedOrgId] = useState(organizationid)
  const [trendToDelete, setTrendToDelete] = useState(null)
  const [graphClickData, setGraphClickData] = useState(null)
  const [exportingTrendIds, setExportingTrendIds] = useState([])
  const [openFiltersRequested, setOpenFiltersRequested] = useState(false)
  const [filtersSeeded, setFiltersSeeded] = useState(false)
  const exportAbortControllersRef = useRef({})

  const isOmniChannelEnabled = useMemo(() => {
    if (isBaltoAdmin) {
      const selectedOrg = organizations?.find((org) => org.id === selectedOrgId)
      return hasAnyOmnichannelAccess(selectedOrg?.active_channels)
    }
    return hasAnyOmnichannelAccess(activeChannels)
  }, [organizations, selectedOrgId])

  // The board shares the Call Explorer filter slice, the way Discover does, so
  // the same bar drives all three.
  const { filters: trendFilters, data: callSearchData } = useSelector((state) => state.callSearch)
  // event filters are expressed as playbook config contexts, so the same config
  // the filter bar built them from has to resolve them for the request
  const { selectedSavedFilterId } = callSearchData

  // An untouched board keeps its original GET; filters only ride along once the
  // user has actually set one, so the unfiltered request is unchanged.
  // The bar writes each selection to the slice as it is made, so the board
  // reloads when the user commits - applying the modal, changing or removing a
  // pill, saving a date range, switching organization - not on every keystroke
  // along the way. Anything else would issue a request per intermediate choice.
  const handleApplyFilters = () => {
    dispatch(fetchTrendsFromLatestFilters())
  }

  const handleSelectSavedFilter = (savedFilterId) => {
    if (savedFilterId) {
      dispatch(setSavedFilterById(savedFilterId, false))
    } else {
      dispatch(clearSavedFilter(false))
    }
  }

  const now = new Date()
  const today = format(now, "yyyy-MM-dd'T23:59:59'xxx")
  const sixtyDaysAgo = format(subDays(now, 59), "yyyy-MM-dd'T00:00:00'xxx")
  const { startDate: effectiveStart, endDate: effectiveEnd, dateRange: pickedPreset } = trendFilters

  const buildDaysForXAxis = (endDate = moment(), days = 60) => {
    const dates = []
    for (let i = 0; i < days; i++) {
      const date = moment(endDate).subtract(i, 'days').format('MM/DD/YYYY')
      dates.push(date)
    }
    setLastNDays(dates.reverse())
  }

  const buildCategoriesForYAxis = (data) => {
    const categories = []

    if (!isEmpty(data)) {
      data.forEach((item) => {
        if (item.category !== NOT_APPLICABLE_CATEGORY) {
          categories.push(item.category)
        }
      })
    }

    return categories
  }

  const getCallType = (filters) => {
    if (!isNil(filters) && filters.call_types && filters.call_types.length === 1) {
      if (filters.call_types[0] === 'call') {
        return 'call'
      }
      return `${filters.call_types[0]} thread`
    }
    return 'call'
  }

  // A trend has nothing to draw either because nothing was sampled, or because
  // everything sampled landed in the "Not Applicable" bucket. The second still
  // has calls behind it, and saying so is the difference between "your filters
  // matched nothing" and "nothing we looked at related to this".
  const noDataReason = (trend) => {
    if (isEmpty(trend.data)) {
      return { kind: 'noMatchingCalls' }
    }
    if (!isEmpty(plottableTrendSeries(trend))) {
      return null
    }
    return {
      kind: 'allIrrelevant',
      callCount: trend.total_calls,
      callType: getCallType(trend.display_filters),
    }
  }

  const processFilterPills = (filters) => {
    const MAX_VALUES = 5
    if (!isNil(filters) && filters.call_types && isOmniChannelEnabled) {
      return [
        <Pill small outlined className="mr" key={uuid()}>
          {`Channel: ${startCase(filters.call_types)}`}
        </Pill>,
      ]
    }
    const result = map(filters, (value, key) => {
      if (!isNull(value)) {
        if (key === 'call_types') {
          return ''
        }
        if (key === 'playbooks' || key === 'dispositions') {
          return (
            <Pill small outlined className="mr" key={uuid()}>
              {value.length <= MAX_VALUES && (
                <span>{`${startCase(key)}: ${value.join(', ')}`}</span>
              )}
              {value.length > MAX_VALUES && (
                <span
                  title={`${value.join(', ')}`}
                >{`${startCase(key)}: ${value.length} selected`}</span>
              )}
            </Pill>
          )
        }
        return (
          <Pill small outlined className="mr" key={uuid()}>
            {`${startCase(key)}: ${value} ${pluralize('minute', value)}`}
          </Pill>
        )
      }
      return ''
    })

    return result
  }

  const processSeriesData = (trendData) => {
    const defaultLine = {
      type: 'line',
      smooth: true,
      symbolSize: 8,
      symbol: 'circle',
      lineStyle: { width: 3, type: 'solid' },
    }

    const seriesData = trendData
      .filter((data) => data.category !== NOT_APPLICABLE_CATEGORY)
      .map((data, index) => {
        const lineChartData = data.data.map((item) => {
          return {
            // Anchor each point to its date on the category x-axis. Without an explicit
            // x value, ECharts maps points by array index, shifting partial data ranges
            // onto the wrong (oldest) date slots.
            value: [moment(item.date_time).format('MM/DD/YYYY'), item.value],
            meta: {
              samples: data.samples,
              description: data.description,
            },
          }
        })
        return {
          ...defaultLine,
          triggerEvent: true,
          name: data.category,
          data: lineChartData,
          groupId: snakeCase(data.category),
          color: chartColors[index % chartColors.length],
          tooltip: nativeTooltip({
            valueFormatter: (value) =>
              formatPercent((Array.isArray(value) ? value[1] : value) * 100, 0),
          }),
        }
      })

    return seriesData
  }

  const processBarSeriesData = (trendData) => {
    const nonApplicableCalls = []
    const filteredData = trendData.filter((item) => {
      if (item.category === NOT_APPLICABLE_CATEGORY) {
        nonApplicableCalls.push(item.num_calls)
        return false
      }
      return true
    })
    const barSeriesData = filteredData.map((item, index) => {
      return {
        value: item.num_calls,
        index,
        itemStyle: { color: chartColors[index % chartColors.length] },
        meta: {
          samples: item.samples,
          description: item.description,
        },
      }
    })

    const barSeriesOption = {
      type: 'bar',
      data: barSeriesData,
      itemStyle: {
        borderRadius: [0, 7, 7, 0],
      },
      label: {
        show: !(trendData.length > MAX_CATEGORIES),
        position: [5, -15],
        formatter: (params) => params.name,
        align: 'left',
      },
    }

    return { barSeriesOption, nonApplicableCalls }
  }

  // These reload from the committed filters, so the board keeps whatever scope
  // it is on and follows the selected organization rather than the viewer's own.
  const handleDeleteTrend = async () => {
    await dispatch(deleteTrend(trendToDelete))
    handleApplyFilters()
    await dispatch(fetchTrendCountForOrg(selectedOrgId))
    dispatch(closeModal())
  }

  const handleUpdateTrendName = async (trendId, name) => {
    await dispatch(updateTrendName(trendId, name))
    handleApplyFilters()
  }

  const handleExportTrend = async (trend) => {
    const startDate = effectiveStart
    const endDate = effectiveEnd
    sendFullStoryEvent('Trends Insight Export clicked', {
      trendName: trend.name,
      organizationId: trend.organization_id,
    })
    const abortController = new AbortController()
    exportAbortControllersRef.current[trend.id] = abortController
    setExportingTrendIds((trendIds) => [...trendIds, trend.id])
    try {
      await dispatch(downloadTrendCallsCsv(trend, startDate, endDate, abortController))
    } finally {
      delete exportAbortControllersRef.current[trend.id]
      setExportingTrendIds((trendIds) => trendIds.filter((trendId) => trendId !== trend.id))
    }
  }

  useEffect(() => {
    // Cancel any in-flight exports when the page unmounts — the stream keeps
    // downloading (and toasting) otherwise.
    return () => {
      Object.values(exportAbortControllersRef.current).forEach((controller) => {
        controller.abort()
      })
    }
  }, [])

  useEffect(() => {
    dispatch(fetchTrendCountForOrg(organizationid))
    // The shared slice carries Call Explorer's defaults, which are not the
    // board's: it opens on today rather than the last 60 days, and applies a
    // one-minute duration floor that would silently drop short calls the board
    // has always counted. Seed both rather than inherit them.
    dispatch(clearAllFilters())
    dispatch(setDateRangeFilters({ startDate: sixtyDaysAgo, endDate: today }))
    dispatch(setFilters({ callDuration: '' }))
    dispatch(fetchSavedFilters(CALL_EXPLORER_FILTER_TYPE))
    buildDaysForXAxis()
    setFiltersSeeded(true)
  }, [])

  useEffect(() => {
    if (isEmpty(effectiveStart) || isEmpty(effectiveEnd)) {
      return
    }
    buildDaysForXAxis(
      effectiveEnd,
      differenceInDays(new Date(effectiveEnd), new Date(effectiveStart))
    )
  }, [effectiveStart, effectiveEnd])

  // clearAllFilters restores the shared slice's defaults, which are Call
  // Explorer's: a one-minute duration floor and a window of today. Neither is
  // the board's, so clearing re-seeds both - dropping filters should not
  // silently reintroduce one or move the user off the range they are reading.
  const handleClearFilters = () => {
    dispatch(clearAllFilters(selectedOrgId))
    dispatch(
      setDateRangeFilters({
        startDate: effectiveStart,
        endDate: effectiveEnd,
        dateRange: pickedPreset,
      })
    )
    dispatch(setFilters({ callDuration: '' }))
  }

  // The first load, once the board's own defaults are in place.
  useEffect(() => {
    if (!filtersSeeded) {
      return
    }
    handleApplyFilters()
  }, [filtersSeeded])

  // Organization is the one change the board tracks itself, for the trend count
  // and the View Calls link.
  useEffect(() => {
    const orgId = trendFilters.organizationId || organizationid
    if (orgId !== selectedOrgId) {
      setSelectedOrgId(orgId)
      dispatch(fetchTrendCountForOrg(orgId))
    }
  }, [trendFilters.organizationId])

  useEffect(() => {
    if (!isEmpty(trends)) {
      const newNonApplicableCounts = []
      const lineChartData = trends.map((trend) => {
        if (trend.backfill_status === 'completed') {
          const trendDataSortedByNumCalls = sortBy(trend.data, 'num_calls')
          return {
            label: trend.name,
            dataGroupId: '',
            color: chartColors,
            tooltip: nativeTooltip({ trigger: 'axis' }),
            legend: {
              show: true,
              top: 0,
              type: trendDataSortedByNumCalls.length > MAX_CATEGORIES ? 'scroll' : 'plain',
            },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: lastNDays,
              axisLine: { show: true },
              splitLine: { show: false },
            },
            yAxis: {
              type: 'value',
              axisLine: { show: false },
              splitLine: { show: false },
              axisLabel: {
                formatter: (val) => formatPercent(val * 100, 0, true),
              },
            },
            emphasis: { focus: 'series' },
            series: processSeriesData(trendDataSortedByNumCalls),
          }
        }

        return null
      })

      const barChartData = trends.map((trend) => {
        if (trend.backfill_status === 'completed') {
          const trendDataSortedByNumCalls = sortBy(trend.data, 'num_calls')

          return {
            label: trend.name,
            dataGroupId: '',
            color: chartColors,
            tooltip: nativeTooltip({ trigger: 'axis' }),
            legend: { show: true, top: 0 },
            grid: {
              left: '4%',
              right: '4%',
              bottom: '3%',
              containLabel: true,
            },
            xAxis: {
              type: 'value',
              boundaryGap: false,
              axisLine: { show: true },
              splitLine: { show: false },
            },
            yAxis: {
              type: 'category',
              data: buildCategoriesForYAxis(trendDataSortedByNumCalls),
              axisLabel: {
                show: false,
              },
              axisTick: {
                show: false,
              },
            },
            emphasis: { focus: 'series' },
            series: (() => {
              const { barSeriesOption, nonApplicableCalls } =
                processBarSeriesData(trendDataSortedByNumCalls)
              newNonApplicableCounts.push(nonApplicableCalls)
              return barSeriesOption
            })(),
          }
        }

        return null
      })

      setCountOfNonApplicableCalls(newNonApplicableCounts)
      setLineChartData(lineChartData)
      setBarChartData(barChartData)
    }
  }, [trends, lastNDays])

  useEffect(() => {
    if (trendToDelete) {
      dispatch(openModal('deleteTrendModal'))
    }
  }, [trendToDelete])

  const handleTrendGraphClick = (params, trend) => {
    const categoryName = params.componentSubType === 'bar' ? params.name : params.seriesName

    const graphData = {
      description: params.data?.meta?.description,
      samples: params.data?.meta?.samples,
      name: categoryName,
      trendQuestion: trend?.question,
      trendName: trend?.name,
      preconditionStatements: trend?.precondition_statements,
    }
    setGraphClickData(graphData)

    sendFullStoryEvent('Trends Insight Chart clicked', {
      chartType: params.componentSubType,
      category: categoryName,
      description: params.data?.meta?.description,
      sampleCount: params.data?.meta?.samples?.length || 0,
    })
  }

  const handleDrawerClose = () => {
    setGraphClickData(null)
    dispatch(resetChatFeed())
  }

  return (
    <>
      {graphClickData && (
        <TrendEvidenceDrawer
          graphClickData={graphClickData}
          onClose={handleDrawerClose}
          organizationId={selectedOrgId}
        />
      )}
      <PageHeader
        data-testid="insights-page"
        decoration="lightning-bolt"
        title="Trends & Insights"
        actions={
          <div className="flex-align-center small-gap">
            <SavedFilters
              filterType={CALL_EXPLORER_FILTER_TYPE}
              selectedSavedFilterId={selectedSavedFilterId}
              handleSelectSavedFilter={handleSelectSavedFilter}
              currentChannel={trendFilters.channel}
              filterFormOptions={SAVED_FILTER_FORM_OPTIONS}
            />
            <Link
              to="/discover"
              className="balto-button brand icon"
              data-testid="new-insight-button"
            >
              <IconSparkles />
              New Insight
            </Link>
          </div>
        }
      />
      <BasicModal
        data-testid="confirm-delete-modal"
        title="Delete Trend"
        onClose={() => dispatch(closeModal())}
        show={currentlyOpenModalId === 'deleteTrendModal'}
        size="tiny"
      >
        <p>Are you sure you want to delete this trend?</p>
        <div className="modal-footer">
          <Button onClick={() => dispatch(closeModal())} data-testid="cancel-change-button">
            Cancel
          </Button>
          <Button
            destructive
            onClick={() => handleDeleteTrend()}
            data-testid="confirm-delete-button"
          >
            Delete
          </Button>
        </div>
      </BasicModal>
      <CallSearchFilters
        allow120Days
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        openFiltersRequested={openFiltersRequested}
        onOpenFiltersHandled={() => setOpenFiltersRequested(false)}
        hiddenFilters={[KEYWORD_FILTERS, AI_AGENT_FILTERS, CHANNEL_FILTER]}
      />

      {trendsLoading && (
        <div className="empty">
          <Loader centered data-testid="trends-loading" />
        </div>
      )}

      {!allow_creation && (
        <Banner
          informative
          header="Limit Reached"
          icon={<IconInfoCircle />}
          dataTestId="limit-reached-banner"
        >
          You have hit the limit of trends available on your plan. Reach out to your Customer
          Success Manager if you would like to add more.
        </Banner>
      )}

      {isEmpty(trends) && !trendsLoading && (
        <div data-testid="trends-no-data" className="mt mb">
          <NoData
            headerText="No trends available"
            text="Start by creating a trend on the Discover page. Once you save it, you'll see it here."
          />
          <div className="flex medium-gap flex-center mt">
            <Link to="/discover" className="balto-button brand no-wrap">
              <IconSparkles />
              New Insight
            </Link>
          </div>
        </div>
      )}

      {trends?.map((trend, index) => {
        return (
          <div key={trend.id}>
            {trend.backfill_status !== 'completed' && (
              <Card
                className="mbl"
                data-testid="pending-trend-card"
                title={
                  <div className="flex-space-between flex-align-center">
                    <EditableHeader
                      initialName={trend.name}
                      updateName={(event, value) => {
                        const { name } = value
                        handleUpdateTrendName(trend.id, name)
                      }}
                    />
                  </div>
                }
                buttons={
                  <Button
                    square
                    small
                    critical
                    onClick={() => {
                      setTrendToDelete(trend.id)
                    }}
                    tooltip="Delete trend"
                  >
                    <IconTrash className="icon-svg" data-testid="delete-trend-icon" />
                  </Button>
                }
                compact
                pills={
                  <div className="flex flex-align-center">
                    <div className="muted-text mr">Generated from </div>

                    {processFilterPills(trend.display_filters).map((pill) => {
                      return pill
                    })}
                  </div>
                }
              >
                <div className="muted-text mtl">
                  We are analyzing recent calls and uncovering valuable insights for you. This might
                  take a few minutes, please check back later.
                </div>
              </Card>
            )}
            {trend.backfill_status === 'completed' && (
              <div className="mbl">
                {noDataReason(trend) && (
                  <Card
                    className="mbl"
                    title={
                      <EditableHeader
                        initialName={trend.name}
                        updateName={(event, value) => {
                          const { name } = value
                          handleUpdateTrendName(trend.id, name)
                        }}
                      />
                    }
                  >
                    <TrendNoDataState
                      reason={noDataReason(trend)}
                      trendName={trend.name}
                      dateRangeLabel={getTriggerLabel(
                        effectiveStart,
                        effectiveEnd,
                        true,
                        true,
                        pickedPreset
                      )}
                      onAdjustFilters={() => setOpenFiltersRequested(true)}
                      onClearFilters={() => {
                        handleClearFilters()
                        handleApplyFilters()
                      }}
                    />
                  </Card>
                )}
                {!noDataReason(trend) && !isEmpty(lineChartData) && (
                  <InsightsModule
                    countNonApplicableCalls={countOfNonApplicableCalls[index]}
                    lineChartOption={lineChartData[index]}
                    barChartOption={barChartData[index]}
                    drilldownOption={null}
                    handleGoBack={() => {}}
                    handleDrillDown={(params) => {
                      handleTrendGraphClick(params, trend)
                    }}
                    trend={trend}
                    callType={getCallType(trend.display_filters)}
                    onUpdateTrendName={(event, value) => {
                      const { name } = value
                      handleUpdateTrendName(trend.id, name)
                    }}
                    onDeleteTrend={() => {
                      setTrendToDelete(trend.id)
                    }}
                    onExportTrend={() => {
                      handleExportTrend(trend)
                    }}
                    exportLoading={exportingTrendIds.includes(trend.id)}
                    pills={
                      <div className="flex flex-align-center">
                        <div className="muted-text mr">Generated from </div>

                        {processFilterPills(trend.display_filters).map((pill) => {
                          return pill
                        })}
                      </div>
                    }
                  />
                )}
              </div>
            )}
          </div>
        )
      })}
      <SentimentModule lastNDays={lastNDays} organizationId={selectedOrgId} />
    </>
  )
}

export default InsightsPage
